import React, { useState, useRef, useEffect } from 'react';
import {
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableFooter,
  TablePagination,
  Menu,
  MenuItem,
  InputAdornment,
} from '@material-ui/core';
import CreateIcon from '@mui/icons-material/Create';
import SearchIcon from '@material-ui/icons/Search';
import { AddCircle, DragIndicator, MoreHoriz, RemoveCircle } from '@material-ui/icons';
import DeleteForeverIcon from '@material-ui/icons/DeleteForever';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CustomChip from '../../../../../shared/form-controls/custom-chip/Custom-Chip';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import TablePaginationActions from '../../../../../shared/table-pagination/TablePaginationActions';
import { handleOnDragEnd } from '../../../../../shared/custom-dragdrop/dragDrop';
import { useDispatch, useSelector } from 'react-redux';
import {
  allPreferencesSelector,
  fetchAllAccommodations,
  setAllPreferences,
} from '../../../../property/propertySlice';
import {
  RBAC_PERMISSION_LEVEL_FULL_ACCESS,
  RBAC_TYPE_HOTEL_ACCOMMODATIONS,
} from '../../../../../constants/RoleConsts';
import './PreferenceList.scss';
import {
  PREFERENCE_CREATE,
  PREFERENCE_EDIT,
  PREFERENCE_VIEW,
} from '../../../../../constants/AccomodationConst';
import {
  deletePreferenceAPI,
  updatePreferenceStatusAPI,
} from '../../../../../utils/ServicesAPIUtils';
import { decryptLocalStorage } from '../../../../../utils/authUtils';
import { notificationManager } from '../../../../../utils/NotificationUtils';
import { CONFIRM_DELETE_TITLE } from '../../../../../utils/Constants';
import DeletePopupModel from '../../../../../shared/delete-popup/Delete-Popup';
import { SaveButton } from '../../../../../shared/custom-dragdrop/Save-Button';
import { createDataSortMapping } from '../../../../../utils/PropertyAPIUtils';
import useOutsideAlerter from '../../../../../custom-hooks/useOutsideAlerter';

const PreferenceList = (props) => {
  const {
    onDrawerToggle,
    onViewToggle,
    selectedHotel,
    permissions,
    page,
    rowsPerPage,
    onsetRowsPerPage,
    onsetPage,
  } = props;

  const dispatch = useDispatch();
  const prevHotelIdRef = useRef(selectedHotel);
  const preferences = useSelector(allPreferencesSelector);

  useEffect(() => {
    if (selectedHotel) {
    }
  }, [dispatch, selectedHotel]);

  const [preferenceList, setPreferenceList] = useState([]);

  useEffect(() => {
    setPreferenceList(preferences);
  }, [preferences]);

  useEffect(() => {
    if (preferenceList?.length === 0) return;
    const totalItems = preferenceList?.length;
    const maxPageIndex = Math.max(Math.ceil(totalItems / rowsPerPage) - 1, 0);
    if (page > maxPageIndex) {
      onsetPage(maxPageIndex);
    }
  }, [preferenceList?.length, rowsPerPage, page, onsetPage]);

  useEffect(() => {
    if (prevHotelIdRef?.current !== selectedHotel) {
      onsetPage(0);
    }
    prevHotelIdRef.current = selectedHotel;
  }, [selectedHotel]);

  const [searchText, setSearchText] = useState('');
  const [visible, setVisible] = useState(false);
  const [popUp, setPopUp] = useState(false);
  const [selectedValue, setSelectedValue] = useState({ id: '', name: '', anchorEl: null });
  const [isAllSelected, setIsAllSelected] = useState(false);
  const searchRef = useRef(null);
  const [deletePopup, setDeletePopup] = useState({
    id: '',
    title: '',
    description: '',
    status: false,
  });
  const wrapperRef = useRef(null);
  useOutsideAlerter(wrapperRef, visible, setPopUp);

  const dragCellWidthsRef = useRef([]);
  const dragDisabled = !!searchText || preferenceList?.length <= 1;

  const handleChangePage = (event, newPage) => {
    onsetPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    onsetRowsPerPage(parseInt(event.target.value, 10));
    onsetPage(0);
  };

  const handleStatusUpdate = async (item) => {
    try {
      const res = await updatePreferenceStatusAPI({
        hotelId: selectedHotel,
        id: item.id,
        lang: decryptLocalStorage('lang'),
      });

      if (res?.data?.updatePreferenceStatus?.status) {
        dispatch(fetchAllAccommodations(selectedHotel));
        notificationManager('success', 'Preference status updated successfully');
        handleClose();
      } else {
        notificationManager('danger', 'Failed to update preference status');
      }
    } catch (error) {
      if (error?.graphQLErrors) {
        notificationManager('danger', error?.graphQLErrors[0]?.message);
      } else {
        notificationManager('danger', 'Error updating preference status');
      }
    }
  };

  const handleDeletePreference = async () => {
    try {
      const res = await deletePreferenceAPI({
        hotelId: selectedHotel,
        id: deletePopup.id,
        lang: decryptLocalStorage('lang'),
      });

      if (res?.data?.deletePreference?.status) {
        dispatch(fetchAllAccommodations(selectedHotel));
        notificationManager('success', 'Preference deleted successfully');
        setDeletePopup({
          id: '',
          title: '',
          description: '',
          status: false,
        });
      } else {
        notificationManager('danger', 'Failed to delete preference');
      }
    } catch (error) {
      if (error?.graphQLErrors) {
        notificationManager('danger', error?.graphQLErrors[0]?.message);
      } else {
        notificationManager('danger', 'Error deleting preference');
      }
    }
  };

  const onDeleteCancel = () => {
    setDeletePopup({
      id: '',
      title: '',
      description: '',
      status: false,
    });
  };

  const handleDelete = () => {
    setDeletePopup({
      id: selectedValue.id,
      title: CONFIRM_DELETE_TITLE,
      description: 'This action cannot be reversed.',
      status: true,
    });
    handleClose();
  };

  const handleSearchText = (text) => {
    setSearchText(text);
    const filtered = preferences.filter((item) =>
      item.name.toLowerCase().includes(text.toLowerCase()),
    );
    setPreferenceList(filtered);
  };

  const handleSelectAll = (event) => {
    const checked = event.target.checked;
    setIsAllSelected(checked);
    setPreferenceList(preferenceList.map((item) => ({ ...item, isChecked: checked })));
  };

  const handleCheckboxChange = (id) => {
    setPreferenceList(
      preferenceList.map((item) =>
        item.id === id ? { ...item, isChecked: !item.isChecked } : item,
      ),
    );
  };

  const handleRowClick = (item) => {
    onViewToggle(PREFERENCE_VIEW, true, item);
  };

  const handleCellMenuClick = (event, item) => {
    setSelectedValue({ id: item.id, name: item.name, anchorEl: event.currentTarget, group: item });
  };

  const handleClose = () => {
    setSelectedValue({ id: '', name: '', anchorEl: null, group: null });
  };

  const onBeforeDragStart = (start) => {
    const rows = document.querySelectorAll('.preference-list-table tbody tr');
    const row = rows[start.source.index];
    if (row) {
      const cells = row.querySelectorAll('td');
      dragCellWidthsRef.current = Array.from(cells).map(
        (cell) => cell.getBoundingClientRect().width,
      );
    }
  };

  const onDragAcrossPages = (result) => {
    if (!result.destination) return;

    const sourceIdx = result.source.index + page * rowsPerPage;
    const destIdx = result.destination.index + page * rowsPerPage;

    handleOnDragEnd(
      {
        ...result,
        source: { ...result.source, index: sourceIdx },
        destination: { ...result.destination, index: destIdx },
      },
      setVisible,
      preferenceList,
      setPreferenceList,
    );
  };
  return (
    <div>
      <div className='row m-2 d-flex mt-4'>
        <div
          className='col-md-4'
          onClick={() => {
            if (visible) setPopUp(true);
          }}
        >
          {deletePopup.status && (
            <DeletePopupModel
              deletePopUp={deletePopup}
              onDelete={handleDeletePreference}
              onDeleteCancel={onDeleteCancel}
            />
          )}
          <TextField
            className='w-100'
            onChange={(e) => handleSearchText(e.target.value)}
            label='Search'
            inputRef={searchRef}
            value={searchText}
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <IconButton className='showpopup'>
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </div>
        <div className='col-12 col-lg-8 pt-3 pt-lg-0 d-flex justify-content-end'>
          <SaveButton
            visible={visible}
            setVisible={setVisible}
            popUp={popUp}
            setPopUp={setPopUp}
            placeHolder={preferenceList}
            setPlaceHolder={setPreferenceList}
            globalPlaceHolder={preferences}
            setGlobalPlaceHolder={(list) => dispatch(setAllPreferences(list))}
            dispatch={dispatch}
            mappingApi={createDataSortMapping}
            displayText='Preference'
            module='ACCOMMODATION_PREFERENCE'
          />
          {permissions[RBAC_TYPE_HOTEL_ACCOMMODATIONS] === RBAC_PERMISSION_LEVEL_FULL_ACCESS && (
            <Button
              variant='contained'
              color='primary'
              size='large'
              startIcon={<AddCircle />}
              onClick={() => onDrawerToggle(PREFERENCE_CREATE, true)}
              className='showpopup'
            >
              Add Preference
            </Button>
          )}
        </div>
      </div>

      <div className='row mx-2 d-flex mt-3' ref={wrapperRef}>
        <TableContainer className='preference-list-table'>
          <Table className='list-table' aria-label='preferences'>
            <TableHead>
              <TableRow>
                <TableCell className='w-10 ps-5'></TableCell>
                <TableCell className='w-30'>Name</TableCell>
                <TableCell className='w-30' align='center'>No. of Items</TableCell>
                <TableCell className='w-20' align='center'>Status</TableCell>
                <TableCell className='w-10' />
              </TableRow>
            </TableHead>
            <DragDropContext onBeforeDragStart={onBeforeDragStart} onDragEnd={onDragAcrossPages}>
              <Droppable droppableId='preferences' isDropDisabled={dragDisabled}>
                {(provided) => (
                  <TableBody {...provided.droppableProps} ref={provided.innerRef}>
                    {(rowsPerPage > 0
                      ? preferenceList.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                      : preferenceList
                    ).map((item, index) => (
                      <Draggable
                        key={item.id}
                        draggableId={item.id}
                        index={index}
                        isDragDisabled={dragDisabled}
                      >
                        {(provided, snapshot) => (
                          <TableRow
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            className='border-white pointer'
                            style={{
                              ...provided.draggableProps.style,
                              ...(snapshot.isDragging && {
                                display: 'table',
                              }),
                            }}
                            onClick={(e) => {
                              if (!e.target.closest('.menu-button') && !selectedValue.anchorEl) {
                                handleRowClick(item);
                              }
                            }}
                          >
                            <TableCell className='w-10' style={snapshot.isDragging ? { width: dragCellWidthsRef.current[0] } : {}}>
                              {!dragDisabled && <DragIndicator className='drag-handle' />}
                            </TableCell>
                            <TableCell className='w-30' style={snapshot.isDragging ? { width: dragCellWidthsRef.current[1] } : {}}>{item.name}</TableCell>
                            <TableCell className='w-30' align='center' style={snapshot.isDragging ? { width: dragCellWidthsRef.current[2] } : {}}>
                              {item.preferenceItems?.length || 0}
                            </TableCell>
                            <TableCell className='w-20' align='center' style={snapshot.isDragging ? { width: dragCellWidthsRef.current[3] } : {}}>
                              <CustomChip
                                label={item.isActive ? 'Active' : 'Inactive'}
                                color={item.isActive ? 'primary' : 'default'}
                              />
                            </TableCell>
                            <TableCell className='w-10' align='right' style={snapshot.isDragging ? { width: dragCellWidthsRef.current[4] } : {}}>
                              {permissions[RBAC_TYPE_HOTEL_ACCOMMODATIONS] ===
                                RBAC_PERMISSION_LEVEL_FULL_ACCESS && (
                                <Button
                                  className='menu-button showpopupInner'
                                  onClick={(e) => handleCellMenuClick(e, item)}
                                >
                                  <MoreHoriz className='showpopupInner' />
                                </Button>
                              )}
                              <Menu
                                anchorEl={selectedValue.anchorEl}
                                open={
                                  Boolean(selectedValue.anchorEl) && selectedValue.id === item.id
                                }
                                onClose={handleClose}
                              >
                                <MenuItem
                                  onClick={() => {
                                    handleClose();
                                    handleStatusUpdate(selectedValue.group);
                                  }}
                                >
                                  {item.isActive ? (
                                    <>
                                      <RemoveCircle className='me-2 action-menu-icon' />
                                      Deactivate
                                    </>
                                  ) : (
                                    <>
                                      <AddCircle className='me-2 action-menu-icon' />
                                      Activate
                                    </>
                                  )}
                                </MenuItem>
                                <MenuItem
                                  onClick={() => {
                                    handleClose();
                                    onDrawerToggle(PREFERENCE_EDIT, true, selectedValue.group);
                                  }}
                                >
                                  <CreateIcon className='me-2 action-menu-icon' />
                                  Edit
                                </MenuItem>
                                <MenuItem
                                  onClick={() => {
                                    handleClose();
                                    onViewToggle(PREFERENCE_VIEW, true, selectedValue.group);
                                  }}
                                >
                                  <VisibilityIcon className='me-2 action-menu-icon' />
                                  View
                                </MenuItem>
                                <MenuItem onClick={handleDelete}>
                                  <DeleteForeverIcon className='me-2 action-menu-icon' />
                                  Delete
                                </MenuItem>
                              </Menu>
                            </TableCell>
                          </TableRow>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </TableBody>
                )}
              </Droppable>
            </DragDropContext>
            <TableFooter>
              <TableRow>
                <TablePagination
                   rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
                  count={preferenceList.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </div>
    </div>
  );
};

export default PreferenceList;
